import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import EditIcon from '@/public/icons/approval/edit.svg';
import TrashIcon from '@/public/icons/approval/trash.svg';
import { MyInfoState } from '@/recoil/atom';
import { useSnackbar } from '@/hooks/global-tools';
import useApprovalDecision, {
  getApprovalDecisionErrorMessage,
  type ApprovalDecisionAction,
} from '@/hooks/approval/useApprovalDecision';
import {
  computeActionVisibility,
  isCurrentApproverMeOf,
} from './actionVisibility';
import useDocumentActionHandlers from './useDocumentActionHandlers';
import OpinionDecisionModal from './OpinionDecisionModal';
import {
  DocActionRow,
  DocActionTriggerButton,
  DocDeleteButton,
  DocEditButton,
} from './styles';
import type {
  ApprovalViewApprover,
  ApprovalViewAuthor,
  DocumentStatus,
} from './types';

/**
 * 문서 액션 버튼 row — 편집/삭제(S·R + 작성자 본인),
 * 결재액션(P·H + 현재 결재자 본인 / S + 작성자 본인 = 상신).
 * 셋 다 미노출이면 null 반환 → TitleBlock 보더 유지. 백엔드 v2 DELETE 가 S 미허용 시 422.
 */

type DocumentActionsProps = {
  status: DocumentStatus;
  apvType: number;
  documentId: string;
  author: ApprovalViewAuthor;
  currentApproverOrder?: number;
  approvers: ApprovalViewApprover[];
  /** 호스트(모달)가 in-place 편집 전환을 처리하려는 경우. 미제공 시 router.push 로 페이지 이동. */
  onEdit?: () => void;
  /** 삭제 성공 후 호스트 후처리 (모달 닫기 / 목록 이동 + 리스트 동기화). 미제공 시 no-op. */
  onDeleted?: () => void;
  /** 결재(승인/반려/보류) 성공 후 호출 — 호스트가 코멘트 재검증 + 액션별 후처리(다음 문서). */
  onDecided?: (action: ApprovalDecisionAction) => void;
  /** 상신 성공 후 호출 — 호스트가 외부 목록을 진행중(P)으로 동기화. */
  onSubmitted?: () => void;
};

export {
  computeActionVisibility,
  isCurrentApproverMeOf,
} from './actionVisibility';

const DocumentActions = ({
  status,
  apvType,
  documentId,
  author,
  currentApproverOrder,
  approvers,
  onEdit,
  onDeleted,
  onDecided,
  onSubmitted,
}: DocumentActionsProps) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const { decide, isSubmitting: isDeciding } = useApprovalDecision();
  const [isOpinionOpen, setIsOpinionOpen] = useState(false);
  const myInfo = useAtomValue(MyInfoState);
  const myPassportId = myInfo?.passportId ?? '';

  const isAuthorMe = !!myPassportId && author.passportId === myPassportId;
  const isCurrentApproverMe = isCurrentApproverMeOf(
    approvers,
    currentApproverOrder,
    myPassportId,
    status,
  );

  const { showEdit, showDelete, showActionTrigger } = computeActionVisibility({
    status,
    isAuthorMe,
    isCurrentApproverMe,
    apvType,
  });

  const { handleEdit, handleDelete, handleSubmit, isSubmitting } =
    useDocumentActionHandlers({
      documentId,
      onEdit,
      onDeleted,
      onSubmitted,
    });

  /**
   * 의견입력 모달 제출 처리 — 빈값 가드는 모달 decide() 담당, 여기선 API 호출·토스트만.
   * 실패 시 모달은 열린 채 유지(재시도 가능). 캐시 무효화는 useApprovalDecision 내부 처리.
   */
  const handleDecide = useCallback(
    async (action: ApprovalDecisionAction, opinion: string) => {
      // 임시저장은 결재 액션이 아니라 상신 — 기안자는 결재 차례를 가진 적이 없어 /confirm 이 403.
      // 백엔드가 PUT status=SUBMIT 에 opinion 을 받도록 열어줬으므로 그 경로로 보냄 (PORTAL-13851)
      if (status === 'S') {
        if (await handleSubmit(opinion)) setIsOpinionOpen(false);
        return;
      }
      if (isDeciding) return;
      try {
        await decide(action, documentId, opinion);
        setIsOpinionOpen(false);
        onDecided?.(action);
        const successMessage =
          action === 'reject'
            ? t('결재가 반려되었습니다.')
            : action === 'hold'
            ? t('결재가 보류 되었습니다.')
            : t('결재가 완료되었습니다.');
        showSuccess({ message: successMessage });
      } catch (error) {
        showError({
          message: t(getApprovalDecisionErrorMessage(error, action)),
        });
      }
    },
    [
      status,
      handleSubmit,
      isDeciding,
      decide,
      documentId,
      onDecided,
      showSuccess,
      showError,
      t,
    ],
  );

  if (!showEdit && !showDelete && !showActionTrigger) return null;

  return (
    <DocActionRow>
      {showEdit && (
        <DocEditButton type="button" onClick={handleEdit}>
          <EditIcon />
          {t('편집')}
        </DocEditButton>
      )}
      {showDelete && (
        <DocDeleteButton type="button" onClick={handleDelete}>
          <TrashIcon />
          {t('문서삭제')}
        </DocDeleteButton>
      )}
      {showActionTrigger && (
        <DocActionTriggerButton
          type="button"
          onClick={() => setIsOpinionOpen(true)}
        >
          <EditIcon />
          {t('의견입력 결재, 반려')}
        </DocActionTriggerButton>
      )}
      <OpinionDecisionModal
        open={isOpinionOpen}
        status={status}
        onClose={() => setIsOpinionOpen(false)}
        onDecide={handleDecide}
        submitting={isDeciding || isSubmitting}
      />
    </DocActionRow>
  );
};

export default DocumentActions;
